import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { humanizeSlug } from "@/lib/seo";

interface RouteNotFoundProps {
  /** When the missing URL belongs to a game, links back to that game. */
  gameSlug?: string;
  title?: string;
  description?: string;
}

/**
 * Consistent 404 body used by the root route and every game/history/results
 * route, so an unknown deep link never renders a blank page.
 */
export function RouteNotFound({ gameSlug, title, description }: RouteNotFoundProps) {
  const gameName = gameSlug ? humanizeSlug(gameSlug) : null;

  return (
    <div className="container flex min-h-[60vh] flex-col items-center justify-center py-12 text-center">
      <p className="font-display text-6xl font-bold text-primary">404</p>
      <h1 className="mt-4 font-display text-2xl font-bold tracking-tight md:text-3xl">
        {title ?? (gameName ? `We couldn't find that ${gameName} page` : "Page not found")}
      </h1>
      <p className="mt-2 max-w-lg text-sm text-muted-foreground">
        {description ??
          (gameName
            ? `That ${gameName} draw or history page doesn't exist. Jump back to the game page or check the latest winning numbers.`
            : "The page you're looking for doesn't exist or has been moved. Try the latest draws or head back home.")}
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        {gameSlug ? (
          <>
            <Button asChild>
              <Link to="/games/$gameSlug" params={{ gameSlug }}>{gameName} page</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/games/$gameSlug/history" params={{ gameSlug }}>Full results history</Link>
            </Button>
          </>
        ) : null}
        <Button variant="outline" asChild>
          <Link to="/results">Latest draw results</Link>
        </Button>
        <Button variant="ghost" asChild>
          <Link to="/">Go home</Link>
        </Button>
      </div>
    </div>
  );
}